'use client';

import { useEffect, useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { createClient } from '@/lib/supabase/client';
import {
  getPageVideoDefinition,
  normalizeMediaAssetUrl,
  normalizeVideoUrl,
  type PageVideoKey,
  type PageVideoRecord,
} from '@/lib/pageVideos';

interface UsePageVideoOptions {
  activeOnly?: boolean;
}

export function usePageVideo(pageKey: PageVideoKey, options: UsePageVideoOptions = {}) {
  const { activeOnly = true } = options;
  const { t } = useLanguage();
  const [video, setVideo] = useState<PageVideoRecord | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const definition = getPageVideoDefinition(pageKey);
    if (!definition) {
      setVideo(null);
      setLoading(false);
      return;
    }
    const resolvedDefinition = definition;

    const supabase = createClient();
    let cancelled = false;

    async function loadVideo() {
      setLoading(true);

      const query = supabase
        .from('page_videos')
        .select('page_key, youtube_url, media_url, cover_url, is_active')
        .eq('page_key', pageKey);

      if (activeOnly) {
        query.eq('is_active', true);
      }

      const { data, error } = await query.maybeSingle();

      if (cancelled) return;

      if (error || !data) {
        setVideo(null);
        setLoading(false);
        return;
      }

      const normalizedYoutubeUrl = normalizeVideoUrl(data.youtube_url);
      const normalizedMediaUrl = normalizeMediaAssetUrl(data.media_url);
      const normalizedCoverUrl = normalizeMediaAssetUrl(data.cover_url);

      if (!normalizedYoutubeUrl && !normalizedMediaUrl) {
        setVideo(null);
        setLoading(false);
        return;
      }

      setVideo({
        ...resolvedDefinition,
        label: t(resolvedDefinition.labelKey),
        description: t(resolvedDefinition.descriptionKey),
        youtube_url: normalizedYoutubeUrl || null,
        media_url: normalizedMediaUrl || null,
        cover_url: normalizedCoverUrl || null,
        is_active: Boolean(data.is_active),
      });
      setLoading(false);
    }

    void loadVideo();

    return () => {
      cancelled = true;
    };
  }, [activeOnly, pageKey, t]);

  return { video, loading };
}